import qz from 'qz-tray';

// --- QZ Tray Security Configuration ---

// Provide the public certificate to QZ Tray
qz.security.setCertificatePromise(function(resolve: (certificate: string) => void, reject: (reason?: any) => void) {
  // Fetch the certificate from the public folder
  fetch("/digital-certificate.txt", {cache: 'no-store', headers: {'Content-Type': 'text/plain'}})
    .then(response => {
      if (response.ok) {
        response.text().then(resolve);
      } else {
        response.text().then(reject);
      }
    })
    .catch(reject); // Catch network errors
});

// Set the signature algorithm (SHA512 for QZ Tray 2.1+)
qz.security.setSignatureAlgorithm("SHA512");

// Provide the signature promise function (CLIENT-SIDE SIGNING FOR DEBUGGING ONLY)
qz.security.setSignaturePromise(function(toSign: string) {
  return function(resolve: (signature: string) => void, reject: (reason?: any) => void) {
    // Fetch the private key (INSECURE - FOR DEBUGGING ONLY)
    fetch("/private-key.pem", {cache: 'no-store', headers: {'Content-Type': 'text/plain'}})
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch private key: ${response.statusText}`);
        }
        return response.text();
      })
      .then(privateKeyPEM => {
        try {
          // Use jsrsasign (ensure it's loaded via index.html)
          // @ts-ignore - jsrsasign is loaded globally
          const sig = new KJUR.crypto.Signature({"alg": "SHA512withRSA"});
          sig.init(privateKeyPEM);
          sig.updateString(toSign);
          const hexSignature = sig.sign();

          // Convert hex signature to Base64
          // @ts-ignore - jsrsasign is loaded globally
          const base64Signature = hextob64(hexSignature);
          console.log("Client-side signature generated.");
          resolve(base64Signature);
        } catch (err) {
          console.error("Client-side signing error:", err);
          reject(err);
        }
      })
      .catch(err => {
        console.error("Error fetching/processing private key:", err);
        reject(err);
      });
  };
});

// --- End QZ Tray Security Configuration ---


let isConnected = false;
let selectedPrinter: string | null = null;

/**
 * Initialize QZ Tray connection and optionally restore saved printer.
 */
export async function initQZ(): Promise<void> {
  if (isConnected) return;

  try {
    await qz.websocket.connect();
    isConnected = true;
    console.log('Connected to QZ Tray');

    // Restore saved printer from localStorage if exists
    const savedPrinter = localStorage.getItem('selectedPrinter');
    if (savedPrinter) {
      selectedPrinter = savedPrinter;
      console.log('Restored saved printer:', selectedPrinter);
    } else {
      // Fallback: find first Zebra printer
      selectedPrinter = await qz.printers.find('zebra');
      console.log('Auto-selected Zebra printer:', selectedPrinter);
    }
  } catch (error) {
    console.error('QZ Tray connection error:', error);
  }
}

/**
 * List all available printers.
 */
export async function listPrinters(): Promise<string[]> {
  try {
    if (!isConnected) {
      await initQZ();
    }
    const printers = await qz.printers.find();
    return printers;
  } catch (error) {
    console.error('Error listing printers:', error);
    return [];
  }
}

/**
 * Set the printer to use for printing.
 */
export function setPrinter(printerName: string) {
  selectedPrinter = printerName;
  localStorage.setItem('selectedPrinter', printerName);
  console.log('Selected printer set to:', printerName);
}

/**
 * Print a 1.25x2.25 inch label with a barcode and truncated description.
 * @param identifier String to use as barcode (ASIN or ZB_ID for unmatched books)
 * @param description Text description (book title or LLM description for unmatched books)
 */
export async function printLabel(identifier: string, description: string): Promise<void> {
  try {
    if (!isConnected || !selectedPrinter) {
      await initQZ();
      if (!isConnected || !selectedPrinter) {
        console.error('QZ Tray not connected or printer not found');
        return;
      }
    }

    const truncatedDescription = description.length > 45 ? description.substring(0, 45) : description;

    // ZPL for 1.25x2.25 inch label with barcode and description
    // The description is placed below both the barcode and the human-readable identifier
    // Adjusted font size and removed field block to prevent overlap
    const zpl = `
^XA
^PW457
^LL254
^CF0,30
^FO50,30^BY2
^BCN,100,Y,N,N
^FD${identifier}^FS
^FO50,150^A0,25,25^FD${truncatedDescription}^FS
^PQ1
^XZ
`.trim();

    const config = qz.configs.create(selectedPrinter, {
      units: 'in',
      size: { width: 2.25, height: 1.25 },
      density: 203,
      copies: 1,
      duplex: false,
      colorType: 'blackwhite',
    });

    await qz.print(config, [zpl]);
    console.log('Sent label to printer');
  } catch (error) {
    console.error('Error printing label:', error);
  }
}
